import { estSourceTransformee, type SourceSimple } from "./composition-affichage.js";

/**
 * LE TITRE D'UNE FICHE EST UN TEXTE, jamais un lien ni un choix qu'on choisit
 * dans un menu (decision du fondateur, 02/11/2026).
 *
 * POURQUOI. Un titre `{ champ: "articleId" }` sur un champ `lien` rendait le
 * titre en selecteur de fiche liee : la fiche portait deux gestes sous un seul
 * mot, et le titre d'une liste, d'un kanban ou d'un calendrier n'est de toute
 * facon qu'un texte. Qui veut reprendre le nom de la fiche liee (ou le libelle
 * d'un choix) ecrit le TEXTE CALCULE `{ champ, affichage: "libelle" }`, en
 * lecture seule, et garde le lien dans une carte de la fiche.
 *
 * LA REGLE SE JUGE ICI, UNE FOIS, et trois gardes l'appellent : la CI des
 * apps (`check-app-entities.mjs`), la verification d'une declaration
 * d'instance a l'ECRITURE (`apps-instanciees-schema.ts`) et le Studio. La
 * lecture d'une instance ancienne ne l'appelle jamais : elle doit continuer de
 * monter.
 *
 * CE QU'EST UN LIEN, UN CHOIX : la meme lecture que l'assemblage du socle
 * (`referencesDeclarees` et `liensEntrants`, `packages/ui/src/views/screens.ts`),
 * recopiee et non importee (ce paquet precede le design system). Un champ du
 * jeu d'une porte est un LIEN s'il est declare `lien` (avec cible) ou
 * `personne`, s'il est une colonne d'ecran a `reference` ou de type `personne`,
 * ou s'il est le `par` d'un `liens[]` d'une AUTRE porte qui vise celle-ci (meme
 * pour un natif). C'est un CHOIX s'il declare des options, ou si la source cite
 * un `catalogue`.
 *
 * LA FONCTION EST DEFENSIVE : la CI lui passe le JSON brut avant que les
 * schemas l'aient juge, et une forme illisible n'est pas SA faute (elle ne
 * rend rien dessus, les schemas le disent mieux).
 */

const PREFIXE_SOCLE = "socle:";
const SAC_SUR_MESURE = "customFields.";
/** Les types de champ a options : un choix, quelle que soit la liste. */
const TYPES_A_CHOIX = new Set(["select", "multiselect"]);

type Objet = Record<string, unknown>;
const estObjet = (valeur: unknown): valeur is Objet =>
  valeur !== null && typeof valeur === "object" && !Array.isArray(valeur);
const liste = (valeur: unknown): unknown[] => (Array.isArray(valeur) ? valeur : []);
const texte = (valeur: unknown): string | undefined =>
  typeof valeur === "string" && valeur !== "" ? valeur : undefined;

const nomNu = (nom: string): string => (nom.startsWith(PREFIXE_SOCLE) ? nom.slice(PREFIXE_SOCLE.length) : nom);

/** Le chemin de stockage d'un champ (`cheminDeStockage` du socle) : la clef que tous les bouts partagent. */
function cheminDe(champ: string, declares: ReadonlySet<string>): string {
  const nu = nomNu(champ);
  if (champ.startsWith(PREFIXE_SOCLE)) return nu;
  return declares.has(nu) ? `${SAC_SUR_MESURE}${nu}` : nu;
}

const aDesOptions = (champ: Objet): boolean =>
  TYPES_A_CHOIX.has(String(champ["type"])) || liste(champ["options"]).length > 0;

/** Ce que le titre d'une porte designe, par chemin de stockage : son libelle d'ecran. */
interface Designation {
  genre: "lien" | "choix";
  libelle: string;
}

/** Les liens et les choix du jeu d'une porte, par chemin de stockage. */
function designationsDeLaPorte(ecran: Objet, porte: Objet, champs: readonly Objet[]): Map<string, Designation> {
  const parChemin = new Map<string, Designation>();
  const declares = new Set(champs.map((champ) => String(champ["key"])));
  const poser = (chemin: string, genre: Designation["genre"], libelle: string): void => {
    if (!parChemin.has(chemin)) parChemin.set(chemin, { genre, libelle });
  };
  for (const champ of champs) {
    const cle = texte(champ["key"]);
    if (cle === undefined) continue;
    const libelle = texte(champ["label"]) ?? cle;
    const chemin = cheminDe(cle, declares);
    if (champ["type"] === "personne" || (champ["type"] === "lien" && champ["reference"] !== undefined)) {
      poser(chemin, "lien", libelle);
    } else if (aDesOptions(champ)) {
      poser(chemin, "choix", libelle);
    }
  }
  for (const colonne of liste(ecran["colonnes"])) {
    if (!estObjet(colonne)) continue;
    const champ = texte(colonne["champ"]);
    if (champ === undefined) continue;
    const libelle = texte(colonne["label"]) ?? nomNu(champ);
    const chemin = cheminDe(champ, declares);
    if (colonne["reference"] !== undefined || colonne["type"] === "personne") poser(chemin, "lien", libelle);
    else if (aDesOptions(colonne)) poser(chemin, "choix", libelle);
  }
  /* LE LIEN ENTRANT : une AUTRE porte (qui montre un jeu) dit « les fiches de
     celle-ci, par tel champ » ; c'est donc que ce champ designe sa fiche. */
  for (const autre of liste(ecran["portes"])) {
    if (!estObjet(autre) || autre["id"] === porte["id"] || !estObjet(autre["jeu"])) continue;
    for (const lien of liste(autre["liens"])) {
      if (!estObjet(lien) || lien["porte"] !== porte["id"]) continue;
      const par = texte(lien["par"]);
      if (par === undefined) continue;
      const chemin = cheminDe(par, declares);
      const colonne = liste(ecran["colonnes"]).find(
        (c): c is Objet => estObjet(c) && typeof c["champ"] === "string" && cheminDe(c["champ"], declares) === chemin,
      );
      const libelle = texte(parChemin.get(chemin)?.libelle) ?? texte(colonne?.["label"]) ?? par;
      /* Un lien entrant prime sur un choix declare du meme champ (il designe une fiche). */
      parChemin.set(chemin, { genre: "lien", libelle });
    }
  }
  return parChemin;
}

/** Le titre est-il un champ NU (ni texte compose, ni fonction, ni titre derive) ? */
function champNuDuTitre(titre: unknown): { champ: string; catalogue: boolean } | undefined {
  if (!estObjet(titre)) return undefined;
  const champ = texte(titre["champ"]);
  if (champ === undefined) return undefined;
  if (titre["fn"] !== undefined || titre["role"] !== undefined || titre["separateur"] !== undefined) return undefined;
  if (estSourceTransformee(titre as SourceSimple)) return undefined;
  return { champ, catalogue: titre["catalogue"] !== undefined };
}

/** Un titre fautif, une vue de une porte d'un ecran : de quoi le dire, et de quoi le reconnaitre. */
interface TitreFautif {
  /** Le prefixe « ecran "x", » quand le fichier a plusieurs ecrans, sinon vide. */
  origine: string;
  porte: string;
  vue: string;
  champ: string;
  genre: Designation["genre"];
  libelle: string;
}

/** Ce qui identifie un titre fautif d'une declaration a l'autre : l'ecran, la porte, la vue, le champ. */
const cleDe = (faute: TitreFautif): string => [faute.origine, faute.porte, faute.vue, faute.champ].join("\u0000");

function titresFautifs(champsFichier: unknown, ecransFichier: unknown): TitreFautif[] {
  if (!estObjet(ecransFichier)) return [];
  const jeux = estObjet(champsFichier) ? liste(champsFichier["jeux"]).filter(estObjet) : [];
  const ecrans = liste(ecransFichier["ecrans"]).filter(estObjet);
  const fautes: TitreFautif[] = [];
  for (const ecran of ecrans) {
    const origine = ecrans.length > 1 ? `ecran "${String(ecran["id"])}", ` : "";
    for (const porte of liste(ecran["portes"]).filter(estObjet)) {
      const jeu = estObjet(porte["jeu"]) ? porte["jeu"] : undefined;
      const jeuDeChamps = jeu === undefined ? undefined : jeux.find(
        (candidat) => candidat["family"] === jeu["family"] && candidat["type"] === jeu["type"],
      );
      const champs = liste(jeuDeChamps?.["champs"]).filter(estObjet);
      const designations = designationsDeLaPorte(ecran, porte, champs);
      const detail = estObjet(porte["vb"]) ? porte["vb"] : estObjet(ecran["vb"]) ? ecran["vb"] : {};
      const vuesMontees = new Set<string>(liste(porte["vues"] ?? ecran["vues"]).filter((v): v is string => typeof v === "string"));
      const declares = new Set(champs.map((champ) => String(champ["key"])));
      for (const vue of vuesMontees) {
        const binding = detail[vue];
        const nu = champNuDuTitre(estObjet(binding) ? binding["title"] : undefined);
        if (nu === undefined) continue;
        const designe = designations.get(cheminDe(nu.champ, declares));
        const genre = designe?.genre ?? (nu.catalogue ? "choix" : undefined);
        if (genre === undefined) continue;
        fautes.push({
          origine, porte: String(porte["id"]), vue, champ: nu.champ, genre,
          libelle: designe?.libelle ?? nomNu(nu.champ),
        });
      }
    }
  }
  return fautes;
}

/** Les fautes, une phrase par (porte, faute) : deux vues qui ont la meme faute se disent en une phrase. */
function phrasesDe(fautes: readonly TitreFautif[]): string[] {
  const parPhrase = new Map<string, { debut: string; vues: string[] }>();
  for (const faute of fautes) {
    const consigne = faute.genre === "lien"
      ? `Pour reprendre le nom de la fiche liée, écris { "champ": "${faute.champ}", "affichage": "libelle" } (lecture seule) et garde le lien dans une carte de la fiche.`
      : `Pour reprendre le libellé de l'option, écris { "champ": "${faute.champ}", "affichage": "libelle" } (lecture seule) et garde le choix dans une carte de la fiche.`;
    const phrase = `le titre ne peut pas être le ${faute.genre} "${faute.libelle}" (${faute.champ}) : un titre est un texte. ${consigne}`;
    const debut = `${faute.origine}porte "${faute.porte}", `;
    const cle = debut + phrase;
    const deja = parPhrase.get(cle) ?? { debut, vues: [] };
    deja.vues.push(faute.vue);
    parPhrase.set(cle, deja);
  }
  return [...parPhrase].map(([cle, { debut, vues }]) =>
    `${debut}${vues.length > 1 ? "vues" : "vue"} ${vues.join(", ")} : ${cle.slice(debut.length)}`);
}

/**
 * LES FAUTES DE TITRE d'une declaration d'app : une phrase par faute, vide si la
 * declaration tient. `champsFichier` est le `taki.fields.json` (ou `null`, une
 * app sans champ declare), `ecransFichier` le `taki.screens.json`, tels que
 * `appFieldsFileSchema` et `appScreensFileSchema` les lisent.
 *
 * LE TITRE EFFECTIF D'UNE VUE est celui du `vb` de la porte s'il existe, sinon
 * celui de l'ecran (jamais une fusion : `detailParVue`). Toutes les vues que la
 * porte monte sont jugees, `core.fiche` comprise : c'est le meme role `title`.
 * Deux vues qui ont la meme faute se disent en une phrase.
 */
export function fautesDeTitre(champsFichier: unknown, ecransFichier: unknown): string[] {
  return phrasesDe(titresFautifs(champsFichier, ecransFichier));
}

/**
 * LES FAUTES DE TITRE QU'UNE ECRITURE INTRODUIT : celles de la declaration
 * `apres` qui n'etaient pas deja dans `avant`, la declaration qu'elle remplace
 * (meme ecran, meme porte, meme vue, meme champ). Un titre lien ou choix ecrit
 * AVANT la regle ne bloque ni un changement qui n'y touche pas, ni sa
 * correction : c'est le garde des chemins d'ecriture du Studio, qui n'ont pas
 * tous un geste pour reparer un titre.
 */
export function fautesDeTitreNeuves(
  apres: { champs: unknown; ecrans: unknown },
  avant: { champs: unknown; ecrans: unknown },
): string[] {
  const dejaLa = new Set(titresFautifs(avant.champs, avant.ecrans).map(cleDe));
  return phrasesDe(titresFautifs(apres.champs, apres.ecrans).filter((faute) => !dejaLa.has(cleDe(faute))));
}
